<template>
  <v-app>
    <template v-if="isAuthenticated">
      <!--
        Kostra podľa roez-ui: tmavý rám s ľavým panelom a svetlá zaoblená plocha, ktorá roluje sama
        (rám ostáva na mieste). Na mobile horná lišta, zásuvka s celou navigáciou a spodné menu.
      -->
      <div class="app-shell" :class="{ 'app-shell--mobile': mobile }" data-shell="app">
        <AppSidebar v-if="!mobile" />

        <div class="app-workspace" data-shell="workspace">
          <AppHeader v-if="mobile" @toggle-drawer="drawer = true" />
          <main ref="mainEl" class="app-main" data-shell="main">
            <div class="app-content">
              <!-- Zoznamy s detailom na samostatnej stránke si pri návrate z detailu pamätajú filtre a záložku -->
              <router-view v-slot="{ Component }">
                <keep-alive :include="keepAliveViews">
                  <component :is="Component" />
                </keep-alive>
              </router-view>
            </div>
          </main>

          <!-- Náhľad súboru priamo v aplikácii (prekryje plochu, panely ostanú) -->
          <NahladSuboru />
        </div>

        <!-- Bočný panel stránky (diskusia v detaile požiadavky / úlohy) vedľa plochy cez celú výšku okna -->
        <div v-if="!mobile" v-show="sidePanelVisible" id="app-side-panel" class="app-side" data-shell="side"></div>
      </div>

      <v-navigation-drawer
        v-if="mobile"
        v-model="drawer"
        temporary
        :width="300"
        class="app-drawer"
        :scrim="true"
      >
        <AppSidebar mobile @close="drawer = false" @navigate="drawer = false" />
      </v-navigation-drawer>

      <AppMobileNavigation v-if="mobile" />

      <!-- Dialóg „Nahlásiť chybu“ — otvára ho položka v ľavom paneli -->
      <FeedbackWidget />
    </template>

    <template v-else>
      <router-view />
    </template>

    <!-- Globálny notifikačný systém — tmavá bublina ako v iOS, farbu nesie ikona -->
    <v-snackbar
      v-model="notificationStore.visible"
      :timeout="notificationStore.timeout"
      color="#1d1d1f"
      location="bottom"
      class="app-toast"
      :class="{ 'app-toast--above-tabbar': isAuthenticated && mobile }"
    >
      <div class="app-toast__body">
        <span class="app-toast__icon" :class="`app-toast__icon--${toastType}`">
          <v-icon :icon="toastIcon" size="18" />
        </span>
        <span>{{ notificationStore.text }}</span>
      </div>
      <template #actions>
        <v-btn variant="text" size="small" class="app-toast__close" @click="notificationStore.hide()">
          Zavrieť
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useAuthStore } from './store/auth'
import { useNotificationStore } from './store/notification'
import AppHeader from './components/layout/AppHeader.vue'
import AppSidebar from './components/layout/AppSidebar.vue'
import AppMobileNavigation from './components/AppMobileNavigation.vue'
import FeedbackWidget from './components/common/FeedbackWidget.vue'
import NahladSuboru from './components/viewer/NahladSuboru.vue'
import { mobileDrawerOpen, toggleSidebar } from './composables/useAppNavigation'
import { dockedSidePanels } from './composables/useSidePanel'

// Stránky ponechané v pamäti pri prechode do detailu (názvy komponentov)
const KEEP_ALIVE_VIEWS = ['PoziadavkyView', 'UlohyView']

const TOAST_ICONS = {
  success: 'mdi-check-circle',
  error: 'mdi-alert-circle',
  warning: 'mdi-alert',
  info: 'mdi-information',
}

export default defineComponent({
  name: 'App',
  components: {
    AppHeader,
    AppSidebar,
    AppMobileNavigation,
    FeedbackWidget,
    NahladSuboru,
  },

  setup() {
    const authStore = useAuthStore()
    const notificationStore = useNotificationStore()
    const route = useRoute()
    const { mobile } = useDisplay()
    const mainEl = ref(null)

    const isAuthenticated = computed(() => authStore.isAuthenticated)

    const toastType = computed(() => (TOAST_ICONS[notificationStore.color] ? notificationStore.color : 'info'))
    const toastIcon = computed(() => TOAST_ICONS[toastType.value])
    const sidePanelVisible = computed(() => dockedSidePanels.value > 0)

    // Plocha roluje sama (nie okno) — po prechode na inú stránku ju vrátime hore.
    watch(
      () => route.path,
      () => {
        mobileDrawerOpen.value = false
        if (mainEl.value) mainEl.value.scrollTop = 0
      }
    )

    // ⌘B / Ctrl+B zbalí alebo rozbalí ľavý panel (ako v roez-ui)
    const onKeydown = (event) => {
      if (mobile.value || !isAuthenticated.value) return
      const target = event.target
      if (target && target.closest && target.closest('input, textarea, select, [contenteditable="true"]')) return
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'b') {
        event.preventDefault()
        toggleSidebar()
      }
    }

    // Pri načítaní aplikácie skontrolujeme, či je používateľ prihlásený
    onMounted(() => {
      authStore.checkAuth()
      window.addEventListener('keydown', onKeydown)
    })
    onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

    return {
      isAuthenticated,
      notificationStore,
      mobile,
      mainEl,
      drawer: mobileDrawerOpen,
      toastType,
      toastIcon,
      sidePanelVisible,
      keepAliveViews: KEEP_ALIVE_VIEWS,
    }
  },
})
</script>

<style lang="scss">
/* ---- Desktop: tmavý rám, ľavý panel a zaoblená plocha s vlastným rolovaním */
.app-shell {
  display: flex;
  height: 100vh;
  height: 100dvh;
  overflow: clip;
  background: var(--nav-bg);
}

.app-workspace {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  margin: var(--shell-gap) var(--shell-gap) var(--shell-gap) 0;
  overflow: hidden;
  border-radius: var(--workspace-radius);
  background: var(--surface-app);
}

.app-side {
  display: flex;
  flex: none;
  min-height: 0;
  margin: var(--shell-gap) var(--shell-gap) var(--shell-gap) 0;
}

.app-main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px 28px 48px;
}

.app-content {
  max-width: 1600px;
  margin: 0 auto;
}

/* ---- Mobil: bez rámu, roluje okno (lišta prehliadača sa môže skryť) */
.app-shell--mobile {
  display: block;
  height: auto;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: visible;
  background: var(--surface-app);

  .app-workspace {
    display: block;
    margin: 0;
    overflow: visible;
    border-radius: 0;
  }

  .app-main {
    overflow: visible;
    padding: 16px 14px calc(var(--mobile-tabbar-height) + env(safe-area-inset-bottom, 0px) + 28px);
  }
}

.app-drawer.v-navigation-drawer {
  background: var(--nav-bg);
  border: 0;
  border-radius: 0 20px 20px 0;
  overflow: hidden;
}

/* ---- Notifikácia */
.app-toast .v-snackbar__wrapper {
  min-width: 0;
  max-width: min(560px, calc(100vw - 24px));
  border-radius: 16px;
  box-shadow: 0 16px 40px -12px rgba(0, 0, 0, 0.45);
}

.app-toast--above-tabbar .v-snackbar__wrapper {
  margin-bottom: calc(var(--mobile-tabbar-height) + env(safe-area-inset-bottom, 0px) + 10px);
}

.app-toast__body {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 550;
  line-height: 1.4;
}

.app-toast__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);

  &--success {
    color: var(--brand-green);
  }
  &--error {
    color: #ff8a8a;
  }
  &--warning {
    color: #f6b55a;
  }
  &--info {
    color: var(--brand-blue);
  }
}

.app-toast__close.v-btn {
  color: rgba(255, 255, 255, 0.72) !important;
}
</style>
